import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useFullscreenPage } from "../../contexts/FullscreenPageContext";
import HeaderTabs from "./HeaderTabs";

const BORDER = "1px solid rgba(0, 0, 0, 0.12)";

// Title collapse animation (expandableActions). MUI's `shorter` duration and
// standard easing, inlined so the title/actions sx stay plain objects.
const COLLAPSE_DURATION = 200;
const COLLAPSE_EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

// Standard top row for v2 School views. The bottom border spans the full screen
// width while the content stays inset by the page padding (px lives on the same
// box as the border, so padding insets the content, not the line). The row's
// height is simply its tallest child (a Tabs strip, a button, an input...), and
// every child is centered vertically within it. `title` may be a plain string
// (styled as a heading) or any node (e.g. a back button) rendered as-is.
//
// The title and the right-hand `actions` share the top line: the title grows to
// push the actions to the right, and when `tabs` is present the title and actions
// take equal flex so the tabs strip is centralized between them. The title can
// shrink (minWidth 0, ellipsis) so the actions never wrap below it. On mobile
// `tabs` wraps to its own line below the title/actions row (via `order` + a
// full-width flex-basis), carries a full-bleed top divider matching the bottom
// border, and stays width-bounded so a scrollable Tabs strip scrolls inside it.
// A little top padding keeps the title off the navbar.
//
// When the current page has gone fullscreen (useRegisterFullscreenPage), a back
// arrow renders just before the title on every breakpoint. On desktop the navbar
// stays alongside it; on mobile the navbar is omitted, so this arrow is the page's
// only way back. Portals that don't mount a FullscreenPageProvider (or pages that
// register nothing) get no arrow.
//
// By default the actions share the top line with the title (hugging the right on
// desktop, taking the right half on mobile). `stackActionsOnMobile` opts a header
// out of that on mobile only: the actions break onto their own full-width row
// (below the title, above the tabs) and left-align. Use it when the actions are
// too wide to sit beside the title on a phone (e.g. a term selector plus a long
// strip of class pills). Desktop is unchanged.
//
// `expandableActions` is for headers whose only action is an expandable search
// field. On mobile, while that field (any focusable inside `actions`) holds
// focus, the title fades out and collapses to zero width so the field grows to
// fill the whole row; on blur it fades back. This is driven entirely by CSS
// `:focus-within` on the row - the title and the field never share state, so the
// page just renders its search field into `actions` as usual and flips this flag.
// Desktop is untouched (the rule is scoped under the `md` breakpoint).
export default function PageHeader({
  title,
  tabs,
  activeTab,
  actions,
  expandableActions,
  stackActionsOnMobile,
  sx,
}) {
  // `tabs` may be a ready array of link descriptors ([{ key, label, ... }],
  // rendered through the standard HeaderTabs strip) or an arbitrary node (an
  // escape hatch for a bespoke strip). An empty array counts as no tabs.
  const isTabsArray = Array.isArray(tabs);
  const hasTabs = isTabsArray ? tabs.length > 0 : Boolean(tabs);
  const activeIndex = isTabsArray
    ? tabs.findIndex((t) => t.key === activeTab)
    : -1;
  const tabsNode = isTabsArray ? (
    <HeaderTabs items={tabs} value={activeIndex === -1 ? false : activeIndex} />
  ) : (
    tabs
  );

  // On mobile the actions take an equal half of the row (flex 1, matching the
  // title's flex 1) so a search field can fill that half. On desktop they hug the
  // right edge: equal flex when there are tabs to centralize, otherwise their
  // natural width.
  const actionsFlex = { xs: 1, md: hasTabs ? 1 : "0 0 auto" };

  const navigate = useNavigate();
  const fullscreenPage = useFullscreenPage();

  const titleNode =
    typeof title === "string" ? (
      <Typography
        sx={{
          fontSize: 24,
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }}
      >
        {title}
      </Typography>
    ) : (
      title
    );

  return (
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        pt: { xs: 2, md: 0 },
        // With tabs, the tabs sit on the bottom border, so no bottom padding.
        // Without tabs, mirror the top padding so the centered content band is
        // vertically symmetric instead of hugging the border (which made a
        // tabless header look like it had less bottom padding than a tabs one).
        pb: hasTabs ? 0 : { xs: 2, md: 0 },
        borderBottom: BORDER,
        // The navbar scrolls away with the page, so this header is the persistent
        // top chrome: pin it to the viewport top on every breakpoint (desktop and
        // mobile) while the page scrolls beneath it. The body-coloured background
        // keeps scrolling content from showing through; the z-index sits above
        // page content, mirroring the level the navbar used while it was pinned.
        position: "sticky",
        top: 0,
        zIndex: 1000,
        backgroundColor: (t) => t.custom.layoutBg.body,
        ...sx,
      }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: { xs: 2, md: 3 },
          rowGap: 1.5,
          // Floor the row height so short, action-less headers keep a consistent
          // height; taller children (a Tabs strip) grow past it as before.
          minHeight: 48,
          // Expandable-search headers: on mobile, focus inside the actions slot
          // (the search field) fades the title out and collapses its slot to zero
          // width, and drops the inter-column gap, so the field fills the row. The
          // title slot animates via its own transition (below); here we just flip
          // the target values. The trigger is `:has(.PageHeader-actionsSlot:focus-
          // within)`, not a plain `:focus-within` on the row, so focusing a *tab*
          // (also a row child) does not collapse the title - only the search does.
          // Scoped to mobile, so desktop never reacts.
          ...(expandableActions && {
            [theme.breakpoints.down("md")]: {
              "&:has(.PageHeader-actionsSlot:focus-within)": {
                columnGap: 0,
                "& .PageHeader-titleSlot": {
                  flexGrow: 0,
                  opacity: 0,
                  pointerEvents: "none",
                },
              },
            },
          }),
        })}
      >
        {/* The title (and its optional leading back arrow) share one flex group
            that takes flex 1, so the row's columnGap only spaces this group from
            the tabs/actions. On a fullscreen page the arrow and the title become a
            single back control on every breakpoint (tapping the title navigates
            back too); on mobile the navbar is omitted, so this is the page's only
            way back. Otherwise the title renders plainly. */}
        <Box
          className="PageHeader-titleSlot"
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            // Only matters when expandableActions is set: animate the fade and the
            // flex-grow collapse the row's focus-within rule targets. overflow
            // hidden keeps the title text clipped (not spilling) as it shrinks.
            ...(expandableActions && {
              overflow: "hidden",
              transition: `flex-grow ${COLLAPSE_DURATION}ms ${COLLAPSE_EASING}, opacity ${COLLAPSE_DURATION}ms ${COLLAPSE_EASING}`,
            }),
          }}
        >
          {fullscreenPage ? (
            <Box
              role="button"
              aria-label="Go back"
              onClick={() => navigate(fullscreenPage.backTo)}
              sx={{
                display: "flex",
                alignItems: "center",
                columnGap: 1,
                minWidth: 0,
                cursor: "pointer",
                // Pull the glyph left so it lines up with the page's content edge
                // rather than sitting indented by its own optical padding.
                ml: -0.5,
                "&:hover": { opacity: 0.7 },
              }}
            >
              <ArrowBackRoundedIcon sx={{ flexShrink: 0 }} />
              {titleNode}
            </Box>
          ) : (
            titleNode
          )}
        </Box>

        {hasTabs && (
          // Stretch to the row height and bottom-align the tabs so a Tabs
          // indicator rides the bottom border even when the actions happen to be
          // the taller child. On mobile the tabs sit on their own line, so they
          // get a full-bleed top divider matching the bottom border: the line
          // bleeds out by the page padding (mx) so it reaches both screen edges
          // like the bottom border, then re-insets its own content (px). The
          // flex-basis carries the same extra width so the row still wraps.
          <Box
            sx={{
              order: { xs: 3, md: 0 },
              // Size to the strip's own content on desktop; HeaderTabs caps its
              // own width and reveals arrows past that cap, so the slot never
              // needs a fixed proportion. It can still shrink under row pressure
              // (flexShrink 1), which just hands scrolling back to the strip.
              flexBasis: { xs: "calc(100% + 32px)", md: "auto" },
              flexGrow: 0,
              flexShrink: 1,
              mx: { xs: -2, md: 0 },
              px: { xs: 2, md: 0 },
              borderTop: { xs: BORDER, md: 0 },
              alignSelf: "stretch",
              display: "flex",
              alignItems: "flex-end",
              minWidth: 0,
              maxWidth: { xs: "none", md: "100%" },
            }}
          >
            {tabsNode}
          </Box>
        )}

        {actions && (
          <Box
            className="PageHeader-actionsSlot"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flex: actionsFlex,
              flexShrink: 0,
              order: { xs: 2, md: 0 },
              // Opt-in: on mobile break onto a full-width row of their own (basis
              // 100% forces the wrap; title/tabs keep their orders so this lands
              // between them) and left-align instead of hugging the right. On
              // desktop restore the basis `actionsFlex` uses (0 with tabs, so the
              // slot stays symmetric with the title and the tabs stay centered).
              ...(stackActionsOnMobile && {
                flexBasis: { xs: "100%", md: tabs ? 0 : "auto" },
              }),
              justifyContent: stackActionsOnMobile
                ? { xs: "flex-start", md: "flex-end" }
                : "flex-end",
              // Actions can be wider than their slot (a term selector plus a long
              // strip of class pills, a row of buttons...). Rather than let them
              // push the row wider and clip the rest of the page, let the slot
              // scroll sideways: minWidth 0 lets it hold to its flex share, and
              // the overflow keeps every action reachable. Scrollbar hidden so it
              // reads as chrome, not a scroll region.
              minWidth: 0,
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              // Floor the title/actions band to the full row height so its
              // content centers the same way whether or not `tabs` are present.
              // Without this, a tabs header wraps tabs to a second line and the
              // first line keeps its natural height (content hugs the top), while
              // a tabless header stretches its single line to the 48px minimum
              // (content centers): the same field would sit at two heights.
              minHeight: 48,
              // Tight line height so an action that wraps to two lines (e.g.
              // "Delete conversation") stacks snugly instead of looking loose.
              "& .MuiButton-root": { lineHeight: 1.25 },
            }}
          >
            {actions}
          </Box>
        )}

        {/* Tabs but no actions: reserve a slot the same width as the title so the
            tabs strip stays centered between them, leaving room on the right for
            future actions (e.g. a large menu like School Profile). Desktop only -
            on mobile the tabs already wrap to their own full-width line. */}
        {!actions && hasTabs && (
          <Box
            aria-hidden
            sx={{ flex: 1, order: 0, display: { xs: "none", md: "block" } }}
          />
        )}
      </Box>
    </Box>
  );
}
